import _ from 'lodash';
import {
  BreadcrumbJsonLd,
  FAQPageJsonLd,
  NextSeo,
  ProductJsonLd,
} from 'next-seo';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';

import { IntroSection, FaqSection, BrandSection } from 'components/AgeSections';
import Footer from 'components/Footer';
import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import MobileProductCard from 'components/MobileProductCard';
import MobileTabBar from 'components/MobileTabBar';
import ProductCard from 'components/ProductCard';

import api from 'utils/api';
import go_api from 'utils/go_api';
import { useAppLaunch } from 'contexts/AppLaunch';
import { fetchAgeFavoriteBrands } from 'utils/ageBrands';
import { getAgeGroup } from 'utils/ageGroups';
import { SITE, normalizeProduct, buildProductSchema } from 'utils/ageProducts';
import { fetchCategoryAgeSeo, stripMarkdown } from 'utils/ageSeo';
import useRWD from 'hooks/useRWD';

// 區塊3：細分類（second_category）TOP 5 產品 — 分頁 tab + 卡片（一排多個）
function SubCategoryTop5({
  ageName,
  ageSlug,
  categoryId,
  sections,
  productOriginMap,
  storeMap,
}) {
  const { isLg } = useRWD();
  const [activeIdx, setActiveIdx] = React.useState(0);

  const validSections = React.useMemo(
    () =>
      (sections || []).filter(
        (s) => s && Array.isArray(s.products) && s.products.length > 0
      ),
    [sections]
  );

  if (validSections.length === 0) return null;

  const safeIdx = Math.min(activeIdx, validSections.length - 1);
  const active = validSections[safeIdx];
  const products = active.products.slice(0, 5);
  const subSlug =
    active.second_category_slug || String(active.second_category_id);
  const fullRankingUrl = `/age/${ageSlug}/${categoryId}/${subSlug}`;

  return (
    <section className="pb-12 lg:pb-16">
      <h2 className="pb-6 text-xl font-bold text-[#111928] lg:text-2xl">
        嚴選 10 大 {ageName} 最愛 {active.second_category_name}
      </h2>

      <div className="no-scrollbar mb-6 flex gap-x-8 overflow-x-auto border-b border-gray-200">
        {validSections.map((s, idx) => (
          <button
            key={s.second_category_id || s.second_category_name}
            type="button"
            onClick={() => setActiveIdx(idx)}
            className={
              'shrink-0 whitespace-nowrap pb-3 text-sm transition lg:text-xl ' +
              (idx === safeIdx
                ? 'border-b-2 border-sorra-pink font-bold text-black'
                : 'text-sorra-gray-800')
            }
          >
            {s.second_category_name} TOP 5
          </button>
        ))}
      </div>

      {isLg ? (
        <div className="grid grid-cols-5 gap-6">
          {products.map((p) => (
            <ProductCard
              key={`top-${p.id}`}
              p={p}
              productOriginMap={productOriginMap}
              storeMap={storeMap}
            />
          ))}
        </div>
      ) : (
        <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4">
          {products.map((p) => (
            <div key={`top-m-${p.id}`} className="w-[160px] shrink-0">
              <MobileProductCard
                p={p}
                productOriginMap={productOriginMap}
                storeMap={storeMap}
              />
            </div>
          ))}
        </div>
      )}

      <div className="pt-6">
        <Link
          href={fullRankingUrl}
          prefetch={false}
          className="inline-flex items-center gap-x-1 text-base font-medium text-sorra-pink hover:underline"
        >
          👉 查看完整 10 款 {ageName} 愛用 {active.second_category_name} 排行榜
        </Link>
      </div>
    </section>
  );
}

export default function AgeCategory({
  ageGroup,
  categoryId,
  categoryName: ssrCategoryName,
  sections: ssrSections,
  brands,
  year,
  month,
  seoData,
  seo,
}) {
  const { notifications, categoryTree, productOriginMap, storeMap } =
    useAppLaunch();

  const ageName = ageGroup.display || ageGroup.name;

  // 主分類：以 id 對應單一 first_category（名稱以 category_tree 為準）
  const firstCategory = React.useMemo(
    () => _.find(categoryTree || [], (c) => c.id === categoryId) || null,
    [categoryTree, categoryId]
  );
  const categoryName = (firstCategory && firstCategory.name) || ssrCategoryName;

  // 區塊3：優先用 SSR 從 go-api 取得的 sections；
  // 無資料時以 category_tree 的 second_categories 補骨架（標題＋連結，無產品）
  const sections = React.useMemo(() => {
    if (ssrSections && ssrSections.length > 0) {
      return ssrSections;
    }
    return (firstCategory ? firstCategory.second_categories || [] : []).map(
      (sc) => ({
        second_category_id: sc.id,
        second_category_name: sc.name,
        second_category_slug: String(sc.id),
        products: [],
      })
    );
  }, [ssrSections, firstCategory]);

  const faq = seoData.faq || [];

  // 結構化資料：攤平所有產品供 ItemList / Product 使用（來自 SSR sections）
  const productSchemas = React.useMemo(() => {
    const all = [];
    (ssrSections || []).forEach((sec) => {
      (sec.products || []).forEach((p) =>
        all.push(buildProductSchema(p, sec.second_category_name))
      );
    });
    return all.filter((p) => p.id && p.productName);
  }, [ssrSections]);

  const itemListJsonLd = React.useMemo(() => {
    if (productSchemas.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: productSchemas.map((p, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: p.productName,
        url: `${SITE}/product/${p.id}`,
      })),
    };
  }, [productSchemas]);

  const breadcrumbItems = [
    { position: 1, name: '首頁', item: `${SITE}/` },
    { position: 2, name: '年齡彙總', item: `${SITE}/age-groups` },
    {
      position: 3,
      name: `${ageName}・${categoryName}`,
      item: `${SITE}/age/${ageGroup.slug}/${categoryId}`,
    },
  ];

  const faqStructured = faq.map((f) => ({
    questionName: f.question,
    acceptedAnswerText: f.answer,
  }));

  return (
    <div>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical={`${SITE}/age/${ageGroup.slug}/${categoryId}`}
      />
      {itemListJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
          />
        </Head>
      )}
      <BreadcrumbJsonLd itemListElements={breadcrumbItems} />
      {productSchemas.map((p) =>
        p.aggregateRating || p.offers ? (
          <ProductJsonLd
            key={`product-jsonld-${p.id}`}
            keyOverride={`product-${p.id}`}
            productName={p.productName}
            brand={p.brand}
            description={p.description}
            images={p.images}
            aggregateRating={p.aggregateRating || undefined}
            category={p.category || undefined}
            offers={p.offers || undefined}
          />
        ) : null
      )}
      {faqStructured.length > 0 && (
        <FAQPageJsonLd mainEntity={faqStructured} />
      )}

      <main>
        <Menubar props={{ homeExtend: false, sourceUI: 'AgeCategory' }} />
        <div
          className={
            'globals-centerContentsMargin' +
            (notifications.length > 0 ? ' withNoti' : '')
          }
        >
          <MaxWidthContainer className="px-4">
            <div className="flex w-[100%] flex-wrap items-center gap-1 py-[16px] lg:py-[32px]">
              <Link
                href="/age-groups"
                className="hover:text-sorra-pink"
                prefetch={false}
              >
                年齡彙總
              </Link>
              <span>&gt;</span>
              <span>
                {ageName}・{categoryName}
              </span>
            </div>

            <h1 className="pb-8 text-3xl font-bold text-[#111928] lg:pb-12 lg:text-5xl">
              {ageName} 最真實的 {categoryName} 推薦與評價
            </h1>

            <IntroSection
              heading={`為什麼 ${ageName} 需要專屬的 ${categoryName} 策略？`}
              intro={seoData.intro}
            />

            <FaqSection
              heading={`${ageName} ${categoryName} 常見問題`}
              faq={faq}
            />

            <BrandSection
              ageName={ageName}
              categoryName={categoryName}
              brands={brands}
            />

            <SubCategoryTop5
              ageName={ageName}
              ageSlug={ageGroup.slug}
              categoryId={categoryId}
              sections={sections}
              productOriginMap={productOriginMap}
              storeMap={storeMap}
            />
          </MaxWidthContainer>
        </div>

        <Footer />
        <MobileTabBar props={{}} />
      </main>
    </div>
  );
}

export async function getServerSideProps(ctx) {
  const { ageGroup: ageSlug, category: catSlug } = ctx.params;

  const ageGroup = getAgeGroup(ageSlug);
  const categoryId = parseInt(catSlug, 10);

  if (!ageGroup || Number.isNaN(categoryId)) {
    return { notFound: true };
  }

  const ageName = ageGroup.display || ageGroup.name;

  // 主分類名稱與 second_categories 從 launchData（category_tree）取得，避免寫死名稱
  let categoryName = '';
  let firstCategory = null;
  let launchOk = false;
  try {
    const { data } = await api.get('/api/v2/appLaunch');
    launchOk = true;
    const tree = _.get(data, 'category_tree', []) || [];
    firstCategory = _.find(tree, (c) => c.id === categoryId) || null;
    if (firstCategory) categoryName = firstCategory.name;
  } catch (e) {
    // 取 launchData 失敗：保留空名稱，交由 client 端補上顯示名稱
  }

  // launchData 取得成功但找不到該主分類（未發佈）→ 視為不存在
  if (launchOk && !categoryName) {
    return { notFound: true };
  }

  // 區塊3：單次向 go-api 取該年齡層各細分類 TOP N（回傳已依 second_category 分組）。
  // GET /api/v1/search/products-by-age?age_range={1-6}&first_category={id}&limit=5
  // 回傳 data.categories:[{ secondCategoryId, secondCategoryName, products:[...] }]
  let sections = [];
  if (firstCategory) {
    try {
      const r = await go_api.get('/api/v1/search/products-by-age', {
        params: {
          age_range: ageGroup.ageRange,
          first_category: firstCategory.id,
          limit: 5,
        },
      });
      const categories = _.get(r, 'data.data.categories', []) || [];
      sections = categories
        .map((c) => ({
          second_category_id: c.secondCategoryId,
          second_category_name: c.secondCategoryName,
          second_category_slug: String(c.secondCategoryId),
          products: (c.products || []).map(normalizeProduct),
        }))
        .filter((s) => s.products.length > 0);
    } catch (e) {
      sections = [];
    }
  }

  // 區塊4：該年齡層（主分類層級）最愛 5 大品牌
  const brands = await fetchAgeFavoriteBrands({
    ageRange: ageGroup.ageRange,
    firstCategory: firstCategory ? firstCategory.id : null,
  });

  // 區塊1/2 的 SEO 內容（導入文案 + FAQ）：依 first_category + 年齡取
  const seoData = await fetchCategoryAgeSeo(
    'first',
    categoryId,
    ageGroup.ageRange
  );

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const nameForSeo = categoryName || '美妝護膚';
  const title = `【${ageName}必買】${year}年 TOP 排行榜！${ageName}最愛${nameForSeo}推薦 | Sorra`;
  const description = seoData.intro
    ? stripMarkdown(seoData.intro)
    : `為 ${ageName} 年齡層整理的${nameForSeo}推薦、評價與排行，快速查找適合的美妝護膚產品。`;

  return {
    props: {
      ageGroup,
      categoryId,
      categoryName,
      sections,
      brands,
      year,
      month,
      seoData,
      seo: { title, description },
    },
  };
}
